/** Preserve prices when regenerating variant combinations. */

export type VariantDraft = {
	attrIndices: number[];
	label: string;
	price: string;
	facilityPrices?: Record<string | number, any>;
	groupPrices?: Record<string | number, any>;
	out_of_stock?: boolean;
};

const valuesFromVariant = (variant: VariantDraft, attributes: { attribute_value?: string }[]) => {
	if (variant.attrIndices?.length) {
		const fromIndices = variant.attrIndices
			.map((i) => attributes[i]?.attribute_value)
			.filter(Boolean) as string[];
		if (fromIndices.length > 0) return fromIndices;
	}
	return (variant.label || '')
		.split(' / ')
		.map((s) => s.trim())
		.filter(Boolean);
};

const findBestExistingVariant = (
	newValues: string[],
	existing: VariantDraft[],
	attributes: { attribute_value?: string }[]
): VariantDraft | null => {
	const newLabel = newValues.join(' / ');
	const exact = existing.find((v) => (v.label || '') === newLabel);
	if (exact) return exact;

	let best: VariantDraft | null = null;
	let bestScore = 0;

	for (const v of existing) {
		const oldValues = valuesFromVariant(v, attributes);
		if (oldValues.length === 0) continue;
		// Prefer existing variants whose values are fully contained in the new combo
		// e.g. old "Small" matches new "Small / Red"
		const contained = oldValues.every((val) => newValues.includes(val));
		if (!contained) continue;
		if (oldValues.length > bestScore) {
			best = v;
			bestScore = oldValues.length;
		}
	}

	return best;
};

/**
 * Build cross-product variants from attributes, carrying over price /
 * facility / group overrides from the closest previous variant when possible.
 */
export const buildVariantsPreservingPrices = (
	attributes: { attribute_name: string; attribute_value: string }[],
	existingVariants: VariantDraft[] = []
): { variants: VariantDraft[]; preservedCount: number } => {
	const byType: Record<string, number[]> = {};
	attributes.forEach((attr, idx) => {
		if (!attr.attribute_name || !attr.attribute_value) return;
		if (!byType[attr.attribute_name]) byType[attr.attribute_name] = [];
		byType[attr.attribute_name].push(idx);
	});

	const types = Object.keys(byType);
	if (types.length === 0) return { variants: [], preservedCount: 0 };

	let combos: number[][] = [[]];
	for (const type of types) {
		const next: number[][] = [];
		for (const combo of combos) {
			for (const idx of byType[type]) next.push([...combo, idx]);
		}
		combos = next;
	}

	let preservedCount = 0;
	const variants = combos.map((attrIndices) => {
		const values = attrIndices.map((i) => attributes[i]?.attribute_value || '').filter(Boolean);
		const label = values.join(' / ');
		const match = findBestExistingVariant(values, existingVariants, attributes);
		const hasPrice = match && match.price !== '' && match.price != null;
		if (hasPrice || (match && (Object.keys(match.facilityPrices || {}).length || Object.keys(match.groupPrices || {}).length))) {
			preservedCount += 1;
		}
		return {
			attrIndices,
			label,
			price: match?.price ?? '',
			facilityPrices: { ...(match?.facilityPrices || {}) },
			groupPrices: { ...(match?.groupPrices || {}) },
			out_of_stock: !!match?.out_of_stock,
		};
	});

	return { variants, preservedCount };
};
